import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import BottomNav from './BottomNav';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { AlertTriangle, ChevronLeft, Clock3, Laptop, ShieldCheck, ShieldOff, Wifi } from 'lucide-react';

const ManageSessions = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeOthersOpen, setRevokeOthersOpen] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  const fetchSessions = async ({ preserveFeedback = false } = {}) => {
    try {
      if (!preserveFeedback) {
        setFeedback({ type: '', message: '' });
      }

      const res = await axiosInstance.get('/auth/sessions', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setSessions(res.data.sessions || []);
      setCurrentSessionId(res.data.currentSessionId || null);
    } catch (err) {
      console.error('Failed to fetch sessions', err);
      setFeedback({
        type: 'error',
        message: 'Unable to load your active sessions right now.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchSessions();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  const handleRevoke = async (sessionId) => {
    try {
      setRevokingSessionId(sessionId);
      setFeedback({ type: '', message: '' });
      await axiosInstance.delete(`/auth/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSessions((prev) => prev.filter((session) => session.session_id !== sessionId));
      setFeedback({ type: 'success', message: 'Session revoked successfully.' });
    } catch (err) {
      console.error('Failed to revoke session', err);
      setFeedback({ type: 'error', message: 'Failed to revoke that session.' });
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleRevokeOthers = async () => {
    try {
      setRevokingOthers(true);
      setFeedback({ type: '', message: '' });
      await axiosInstance.delete('/auth/session/revoke-all', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await fetchSessions({ preserveFeedback: true });
      setFeedback({ type: 'success', message: 'All other sessions were revoked.' });
    } catch (err) {
      console.error('Failed to revoke all sessions', err);
      setFeedback({ type: 'error', message: 'Could not revoke your other sessions.' });
    } finally {
      setRevokingOthers(false);
      setRevokeOthersOpen(false);
    }
  };

  const orderedSessions = useMemo(() => {
    const effectiveCurrentSessionId =
      currentSessionId || (sessions.length === 1 ? sessions[0].session_id : null);

    return [...sessions].sort((left, right) => {
      if (left.session_id === effectiveCurrentSessionId) return -1;
      if (right.session_id === effectiveCurrentSessionId) return 1;
      return new Date(right.last_used_at || right.expires_at || 0) - new Date(left.last_used_at || left.expires_at || 0);
    });
  }, [currentSessionId, sessions]);

  const effectiveCurrentSessionId =
    currentSessionId || (sessions.length === 1 ? sessions[0].session_id : null);

  const otherSessionsCount = orderedSessions.filter(
    (session) => session.session_id !== effectiveCurrentSessionId
  ).length;

  const revokeSession = orderedSessions.find(
    (session) => session.session_id === revokeTarget
  ) || null;

  const dialogPaperSx = {
    borderRadius: '24px',
    width: '100%',
    maxWidth: '420px',
    padding: '4px',
    backgroundColor: '#18181b',
    color: '#f5f5f5',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
  };

  const dialogCancelButtonClass = 'min-w-[110px] rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-gray-200 dark:hover:bg-neutral-800';
  const dialogDangerButtonClass = 'min-w-[110px] rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 dark:bg-red-500 dark:hover:bg-red-400 dark:disabled:bg-red-900';

  const sessionDateFormatter = useMemo(
    () => new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    []
  );

  const getSessionTitle = (session) => {
    if (session.session_id === effectiveCurrentSessionId) {
      return 'This device';
    }

    const userAgent = session.user_agent || '';

    if (/iphone|ipad|ios/i.test(userAgent)) return 'Apple device'; //iPhone or iPad
    if (/android/i.test(userAgent)) return 'Android device';
    if (/mac os|macintosh/i.test(userAgent)) return 'Apple device'; //Mac
    if (/windows/i.test(userAgent)) return 'Windows device';

    return 'Signed-in device';
  };

  const formatTimestamp = (value) => {
    if (!value) return 'Unavailable';

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'Unavailable';

    const parts = sessionDateFormatter.formatToParts(parsed);
    const day = parts.find((part) => part.type === 'day')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const year = parts.find((part) => part.type === 'year')?.value;
    const hour = parts.find((part) => part.type === 'hour')?.value;
    const minute = parts.find((part) => part.type === 'minute')?.value;
    const dayPeriod = parts.find((part) => part.type === 'dayPeriod')?.value?.toUpperCase();

    if (!day || !month || !year || !hour || !minute || !dayPeriod) {
      return 'Unavailable';
    }

    return `${day} ${month} ${year} at ${hour}:${minute} ${dayPeriod}`;
  };

  return (
    <>
      <div className="fixed inset-0 z-[1000] mb-16 flex flex-col bg-white dark:bg-black">
        <div className="top-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-8 w-8" />
          </button>
          <h1 className="top-title">Manage Devices</h1>
        </div>

        <div className="tailwind flex-1 overflow-y-auto px-6 py-4 pb-28 text-black dark:text-white">
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
              <section className="rounded-3xl bg-gray-100 p-5 dark:bg-smallDarkCard">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <p className="text-sm font-medium uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Security
                    </p>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Signed-in devices
                    </h2>
                    <p className="max-w-xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                      Review the devices that can access your account and remove any session you do not recognize.
                    </p>
                  </div>

                  <div className="min-w-[12rem] rounded-2xl bg-white px-4 py-3 shadow-sm dark:bg-neutral-900">
                    <p className="text-xs uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Active now
                    </p>
                    <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
                      {orderedSessions.length}
                    </p>
                    {/* <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {otherSessionsCount > 0
                        ? `${otherSessionsCount} other ${otherSessionsCount === 1 ? 'session' : 'sessions'}`
                        : 'Only this device is signed in'}
                    </p> */}
                  </div>
                </div>

                {feedback.message ? (
                  <Alert severity={feedback.type || 'info'} sx={{ mt: 3, borderRadius: 2 }}>
                    {feedback.message}
                  </Alert>
                ) : null}
              </section>

              {orderedSessions.length > 0 ? (
                <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
                  <div>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      Revoke other sessions
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Sign out everywhere else while keeping this device active.
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={otherSessionsCount === 0 || revokingOthers}
                    onClick={() => setRevokeOthersOpen(true)}
                    className="min-w-[170px] rounded-full bg-red-600 px-5 py-2.5 mx-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 disabled:text-red-50 dark:bg-red-500 dark:hover:bg-red-400 dark:disabled:bg-red-900 dark:disabled:text-red-200"
                  >
                    {revokingOthers ? 'Revoking...' : 'Revoke others'}
                  </button>
                </div>
              ) : null}

              {orderedSessions.length === 0 ? (
                <section className="rounded-3xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-neutral-800 dark:bg-neutral-950">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-smallDarkCard">
                    <Laptop className="h-7 w-7 text-gray-500 dark:text-gray-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">No active sessions found</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    When devices sign in to your account, they will appear here.
                  </p>
                </section>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {orderedSessions.map((session) => {
                    const isCurrent = session.session_id === effectiveCurrentSessionId;
                    const isRevokingThis = revokingSessionId === session.session_id;

                    return (
                      <li
                        key={session.session_id}
                        className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-950"
                      >
                        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-700 dark:bg-smallDarkCard dark:text-gray-200">
                                <Laptop className="h-5 w-5" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {getSessionTitle(session)}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {isCurrent ? 'Current device' : 'Remote session'}
                                </p>
                              </div>
                              {isCurrent ? (
                                <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 dark:bg-green-500/15 dark:text-green-300">
                                  Active here
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-4 grid gap-3 text-sm text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                              <div className="rounded-2xl bg-gray-50 px-4 py-3 dark:bg-smallDarkCard">
                                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                                  <Clock3 className="h-4 w-4" />
                                  Last active
                                </div>
                                <p className="font-medium text-gray-800 dark:text-gray-100">
                                  {formatTimestamp(session.last_used_at || session.created_at)}
                                </p>
                              </div>

                              <div className="rounded-2xl bg-gray-50 px-4 py-3 dark:bg-smallDarkCard">
                                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                                  <Wifi className="h-4 w-4" />
                                  IP address
                                </div>
                                <p className="font-medium text-gray-800 dark:text-gray-100">
                                  {session.ip_address || 'Unavailable'}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                                  session.is_trusted_device
                                    ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                                }`}
                              >
                                {session.is_trusted_device ? (
                                  <ShieldCheck className="h-4 w-4" />
                                ) : (
                                  <ShieldOff className="h-4 w-4" />
                                )}
                                {session.is_trusted_device ? 'Trusted device' : 'Untrusted device'}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                                {session.is_persistent ? 'Persistent session' : 'Session expires automatically'}
                              </span>
                            </div>

                            <p className="mt-4 break-words text-sm leading-6 text-gray-500 dark:text-gray-400">
                              {session.user_agent || 'Device details unavailable'}
                            </p>
                          </div>

                          <div className="flex shrink-0 items-center md:pl-4">
                            {isCurrent ? (
                              <div className="rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-700 dark:bg-green-500/15 dark:text-green-300">
                                This device
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={isRevokingThis || revokingOthers}
                                onClick={() => setRevokeTarget(session.session_id)}
                                className="min-w-[120px] rounded-full border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:border-red-400 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 dark:border-red-500/50 dark:text-red-400 dark:hover:border-red-400 dark:hover:bg-red-500/10 dark:disabled:border-neutral-800 dark:disabled:bg-neutral-900 dark:disabled:text-neutral-500"
                              >
                                {isRevokingThis ? 'Revoking...' : 'Revoke'}
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { sx: dialogPaperSx } }}
      >
        <DialogTitle sx={{ px: 3, pb: 1, pt: 3 }}>
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-semibold text-white">Revoke this session?</div>
              <div className="mt-1 text-sm font-normal text-neutral-400">
                {revokeSession ? getSessionTitle(revokeSession) : 'Selected device'}
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <DialogContentText sx={{ m: 0, color: '#b3b3b3', lineHeight: 1.7 }}>
            This device will immediately lose access to your account until it signs in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <button
            type="button"
            onClick={() => setRevokeTarget(null)}
            className={dialogCancelButtonClass}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              const sessionId = revokeTarget;
              setRevokeTarget(null);
              handleRevoke(sessionId);
            }}
            className={dialogDangerButtonClass}
          >
            Revoke
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revokeOthersOpen}
        onClose={() => !revokingOthers && setRevokeOthersOpen(false)}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { sx: dialogPaperSx } }}
      >
        <DialogTitle sx={{ px: 3, pb: 1, pt: 3 }}>
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-semibold text-white">Revoke other sessions?</div>
              <div className="mt-1 text-sm font-normal text-neutral-400">
                Keep this device signed in
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <DialogContentText sx={{ m: 0, color: '#b3b3b3', lineHeight: 1.7 }}>
            This will sign your account out on every other device and browser currently connected to your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <button
            type="button"
            onClick={() => setRevokeOthersOpen(false)}
            disabled={revokingOthers}
            className={dialogCancelButtonClass}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRevokeOthers}
            disabled={revokingOthers}
            className={dialogDangerButtonClass}
          >
            {revokingOthers ? 'Revoking...' : 'Revoke others'}
          </button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </>
  );
};

export default ManageSessions;
